import PageLoader from '@components/PageLoader'
import { countLabel } from '@/utils/plural'
import { useAdminTrip } from '@hooks/useAdminTrip'
import React from 'react'
import { useParams } from 'react-router-dom'
import { legendRows } from '@pages/Itinerary/core/destinations'
import { formatDate, formatName, formatTripDates } from '../adminFormat'
import {
	BackLink,
	Card,
	CardTitle,
	DeletedBadge,
	DetailHeader,
	DetailTitle,
	Field,
	FieldGrid,
	FieldLabel,
	FieldValue,
	RelatedItem,
	RelatedList,
	RelatedMeta,
	RelatedRow,
} from '../adminDetail.styles'
import { EmptyNoteInline } from '@components/EmptyState'
import { ErrorNote } from '@components/Notice'

/**
 * Read-only admin detail for one trip: owner (links to their detail), destinations
 * (labelled as the itinerary legend labels them), members (each links to their
 * detail), live child counts, and dates. No mutations.
 */
const TripDetailPage: React.FC = () => {
	const { id } = useParams<{ id: string }>()
	const { data: trip, isLoading, isError } = useAdminTrip(Number(id))

	return (
		<>
			<BackLink to="/admin/trips">← Back to trips</BackLink>

			{isLoading ? (
				<PageLoader label="Loading trip…" />
			) : isError || !trip ? (
				<ErrorNote>Could not load this trip.</ErrorNote>
			) : (
				<>
					<DetailHeader>
						<DetailTitle>{trip.name || 'Untitled trip'}</DetailTitle>
						{trip.isDeleted && <DeletedBadge>Deleted</DeletedBadge>}
					</DetailHeader>

					<Card>
						<CardTitle>Trip</CardTitle>
						<FieldGrid>
							<Field>
								<FieldLabel>Owner</FieldLabel>
								<FieldValue>
									{formatName(trip.owner.firstName, trip.owner.lastName)} · {trip.owner.email}
								</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Join code</FieldLabel>
								<FieldValue>{trip.code}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Dates</FieldLabel>
								<FieldValue>{formatTripDates(trip.startDate, trip.dayCount)}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Length</FieldLabel>
								<FieldValue>
									{countLabel(trip.dayCount, 'day')}
								</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Default currency</FieldLabel>
								<FieldValue>{trip.defaultCurrencyCode.toUpperCase()}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Visibility</FieldLabel>
								<FieldValue>{trip.isVisibleToPublic ? 'Public' : 'Private'}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Created</FieldLabel>
								<FieldValue>{formatDate(trip.createdAt)}</FieldValue>
							</Field>
						</FieldGrid>
					</Card>

					<Card>
						<CardTitle>Destinations ({trip.destinations.length})</CardTitle>
						{trip.destinations.length === 0 ? (
							<EmptyNoteInline>No destinations.</EmptyNoteInline>
						) : (
							<RelatedList>
								{legendRows(trip.destinations, trip.startDate).map(row => (
									<RelatedItem key={row.id}>
										<span>
											{row.name}
											{row.detail && ` · ${row.detail}`}
										</span>
										<RelatedMeta>{row.dateRange}</RelatedMeta>
									</RelatedItem>
								))}
							</RelatedList>
						)}
					</Card>

					<Card>
						<CardTitle>Contents</CardTitle>
						<FieldGrid>
							<Field>
								<FieldLabel>Members</FieldLabel>
								<FieldValue>{trip.memberCount.toLocaleString()}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Events</FieldLabel>
								<FieldValue>{trip.eventCount.toLocaleString()}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Ideas</FieldLabel>
								<FieldValue>{trip.ideaCount.toLocaleString()}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Expenses</FieldLabel>
								<FieldValue>{trip.expenseCount.toLocaleString()}</FieldValue>
							</Field>
						</FieldGrid>
					</Card>

					<Card>
						<CardTitle>Members ({trip.members.length})</CardTitle>
						{trip.members.length === 0 ? (
							<EmptyNoteInline>This trip has no members.</EmptyNoteInline>
						) : (
							<RelatedList>
								{trip.members.map(member => (
									<RelatedRow
										key={member.id}
										to={`/admin/users/${member.id}`}
										$deleted={member.isDeleted}
									>
										<span>
											{formatName(member.firstName, member.lastName)}
											{member.id === trip.owner.id && ' · owner'}
											{member.isDeleted && ' · left'}
										</span>
										<RelatedMeta>Joined {formatDate(member.joinedAt)}</RelatedMeta>
									</RelatedRow>
								))}
							</RelatedList>
						)}
					</Card>
				</>
			)}
		</>
	)
}

export default TripDetailPage
